import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ShieldCheck } from "lucide-react";

/**
 * /privacy-beta — full GDPR + CNOP informativa for Revela beta testers.
 * Linked from BetaWelcomeModal (Step 4) and Settings.
 */
const PrivacyBetaPage = () => {
  const navigate = useNavigate();

  return (
    <div className="max-w-2xl mx-auto py-8 px-4 space-y-6" data-testid="privacy-beta-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="privacy-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">Informativa Privacy — Beta Revela</h1>
          <p className="text-sm text-muted-foreground">
            Conforme al Regolamento UE 2016/679 (GDPR) e al Codice Deontologico CNOP
          </p>
        </div>
      </div>

      <section className="prose prose-sm max-w-none space-y-5">
        <div>
          <h2 className="text-lg font-semibold">1. Titolare del trattamento</h2>
          <p>
            Dott.ssa <strong>Laura Piccinini</strong>, psicologa iscritta all'Ordine — email di
            contatto: <a className="text-rose-600 underline" href="mailto:piccinini.ps@gmail.com">piccinini.ps@gmail.com</a>.
          </p>
        </div>

        <div>
          <h2 className="text-lg font-semibold">2. Finalità del trattamento</h2>
          <p>
            I dati che inserisci nell'app sono utilizzati per:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>fornirti <strong>visualizzazioni personali</strong> dei tuoi pattern esperienziali (solo tu vedi i tuoi dati);</li>
            <li>permettere a <strong>Laura</strong> di valutare l'usabilità e l'utilità clinica dell'app <em>esclusivamente</em> tramite statistiche aggregate anonimizzate (k≥5: ogni cifra con conteggi inferiori a 5 è mascherata per impedire la re-identificazione);</li>
            <li>raccogliere feedback informali sul beta (tramite messaggi diretti — non automatizzati nell'app).</li>
          </ul>
        </div>

        <div>
          <h2 className="text-lg font-semibold">3. Categorie di dati trattati</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Dati di account:</strong> email, nome, eventuale immagine profilo.</li>
            <li><strong>Dati esperienziali (Experience Tracking):</strong> luogo, contesto sociale, attività, dimensioni emotive (carico, piacevolezza, volontarietà, controllo, qualità sociale), eventuali note testuali/vocali volontarie.</li>
            <li><strong>Dati di utilizzo:</strong> log di accesso, timestamp dei check-in.</li>
          </ul>
          <p className="text-sm">
            I dati esperienziali sono cifrati a riposo con AES-256 (Fernet). Le trascrizioni vocali
            sono cifrate e non sono mai esposte agli amministratori.
          </p>
        </div>

        <div>
          <h2 className="text-lg font-semibold">4. Base giuridica</h2>
          <p>
            Il trattamento avviene sulla base del tuo <strong>consenso esplicito</strong> (Art. 6.1.a
            GDPR), prestato accettando questa informativa al primo accesso. Il consenso è
            <strong> revocabile in qualsiasi momento</strong>, anche cancellando l'account dalle
            Impostazioni.
          </p>
        </div>

        <div>
          <h2 className="text-lg font-semibold">5. Conservazione</h2>
          <p>
            I dati sono conservati per la durata del beta test (presumibilmente 3-6 mesi) e
            comunque non oltre 24 mesi. Su richiesta dell'interessata, o all'eliminazione
            dell'account, i dati personali identificativi sono cancellati immediatamente. I dati
            aggregati anonimi possono essere conservati a scopo di ricerca senza limiti.
          </p>
        </div>

        <div>
          <h2 className="text-lg font-semibold">6. Diritti dell'interessata (Artt. 15-22 GDPR)</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Accesso:</strong> puoi visualizzare tutti i tuoi dati in app.</li>
            <li><strong>Rettifica:</strong> puoi modificare/cancellare singoli check-in dalla timeline.</li>
            <li><strong>Cancellazione (oblio):</strong> Impostazioni → Account → Cancella account elimina tutto, definitivamente.</li>
            <li><strong>Portabilità:</strong> Impostazioni → Esporta dati CSV.</li>
            <li><strong>Opposizione e limitazione:</strong> contatta Laura per esercitare questi diritti.</li>
            <li><strong>Reclamo:</strong> hai diritto di proporre reclamo al Garante per la Protezione dei Dati Personali (<a href="https://www.garanteprivacy.it" className="text-rose-600 underline" target="_blank" rel="noreferrer">garanteprivacy.it</a>).</li>
          </ul>
        </div>

        <div>
          <h2 className="text-lg font-semibold">7. Sicurezza e localizzazione</h2>
          <p>
            I dati sono ospitati su infrastruttura cloud all'interno dell'Unione Europea.
            Sono protetti da cifratura at-rest (AES-256), connessione HTTPS, accesso con
            autenticazione e session token. Nessun dato è trasferito a paesi extra-UE.
          </p>
        </div>

        <div>
          <h2 className="text-lg font-semibold">8. Conformità deontologica (CNOP)</h2>
          <p>
            Pur essendo Revela un'app pilota di tracking esperienziale, e non uno strumento
            psicodiagnostico, il trattamento dei dati segue lo spirito del Codice Deontologico
            CNOP — segreto professionale (Art. 11), tutela del benessere (Art. 3), evitamento
            di confusione di ruoli (Artt. 26-28).
          </p>
        </div>

        <p className="text-xs text-muted-foreground italic">
          Ultima revisione: 21 maggio 2026
        </p>
      </section>
    </div>
  );
};

export default PrivacyBetaPage;
